import { useCallback, useState, useSyncExternalStore } from "react";

/** Which directions a horizontal scroller can still move in. */
export interface ScrollOverflow {
  canScrollPrev: boolean;
  canScrollNext: boolean;
}

const NO_OVERFLOW: ScrollOverflow = {
  canScrollPrev: false,
  canScrollNext: false,
};

// Sub-pixel layout (zoom, fractional widths) can leave scrollWidth a pixel
// past clientWidth, or scrollLeft a pixel short of the end, with nothing to
// actually reveal.
export const EDGE_TOLERANCE = 1;

/** Reads a horizontal scroller's overflow from its own box, not the window. */
export function measureScrollOverflow(
  el: Pick<HTMLElement, "scrollLeft" | "scrollWidth" | "clientWidth">,
): ScrollOverflow {
  const maxScroll = el.scrollWidth - el.clientWidth;
  if (maxScroll <= EDGE_TOLERANCE) return NO_OVERFLOW;
  return {
    canScrollPrev: el.scrollLeft > EDGE_TOLERANCE,
    canScrollNext: el.scrollLeft < maxScroll - EDGE_TOLERANCE,
  };
}

// Snapshots are an index into this table, so an unchanged measurement keeps the
// same object and useSyncExternalStore doesn't re-render.
const STATES: readonly ScrollOverflow[] = [
  NO_OVERFLOW,
  { canScrollPrev: false, canScrollNext: true },
  { canScrollPrev: true, canScrollNext: false },
  { canScrollPrev: true, canScrollNext: true },
];

const toIndex = ({ canScrollPrev, canScrollNext }: ScrollOverflow): number =>
  (canScrollPrev ? 2 : 0) + (canScrollNext ? 1 : 0);

const serverSnapshot = () => 0;

/**
 * Tracks whether a horizontal scroller overflows and which way it can scroll,
 * for showing carousel prev/next controls only when they do something.
 * Re-measures on scroll, when the scroller is resized (including a narrow
 * container such as the editor's mobile preview), and when its content
 * changes. Pass the returned ref callback to the scrolling element.
 */
export function useScrollOverflow<T extends HTMLElement>(): [
  (el: T | null) => void,
  ScrollOverflow,
] {
  const [el, setEl] = useState<T | null>(null);

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!el) return () => {};
      el.addEventListener("scroll", onChange, { passive: true });
      const resize =
        typeof ResizeObserver === "undefined"
          ? null
          : new ResizeObserver(onChange);
      resize?.observe(el);
      // Items added, removed or hidden change scrollWidth without resizing
      // the scroller itself.
      const content =
        typeof MutationObserver === "undefined"
          ? null
          : new MutationObserver(onChange);
      content?.observe(el, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["hidden", "class", "style"],
      });
      return () => {
        el.removeEventListener("scroll", onChange);
        resize?.disconnect();
        content?.disconnect();
      };
    },
    [el],
  );

  const index = useSyncExternalStore(
    subscribe,
    () => (el ? toIndex(measureScrollOverflow(el)) : 0),
    serverSnapshot,
  );

  return [setEl, STATES[index] ?? NO_OVERFLOW];
}
